import { FC, useState } from 'react';
import { z } from 'zod';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';

import { useFieldArray } from 'react-hook-form';
import { ModalContent } from '../../../../modal/components/ModalContent';
import { ModalActions } from '../../../../modal/components/ModalActions';
import { useModalContext } from '../../../../modal/ModalContext';
import { TeamSelect } from './TeamSelect';
import { Button, IconButton, LinearProgress, OutlinedInput, Stack, Typography } from '@mui/material';
import { FormField } from '../../../../form/FormField';
import { ControlledFormField } from '../../../../form/ControlledFormField';
import { FormErrors } from '../../../../form/FormErrors';
import { Add, DeleteOutlineOutlined } from '@mui/icons-material';
import { graphql } from '../../../../gql';
import { useApiMutation } from '../../../../query/hooks';
import { useTypedForm } from '../../../../../../shared/fe/hooks/useForm';
import { mutationResponseHandler } from '../../../../utils/mutationResponseHandler';
import { getNightOrDayPalette } from '../../../../utils/getNightOrDayPalette';
import { useSnackbar } from '../../../../store/snackbar';

const inviteMemberMutation = graphql(`
	mutation organization_inviteMember($email: String!, $name: String!, $teamId: UUID) {
		inviteMember(email: $email, name: $name, teamId: $teamId) {
			ok
			errorMessage
		}
	}
`);

const createFormSchema = (intl: IntlShape) =>
	z.object({
		members: z.array(
			z.object({
				name: z.string().min(1, intl.formatMessage({ id: 'form.settings.invite-member.error.required' })),
				email: z.string().email(intl.formatMessage({ id: 'form.settings.invite-member.error.email-format' })),
				teamId: z.string().nullable(),
			}),
		),
	});

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

const inviteMembersModalSchema = z.object({});

const InviteMembersModal: FC = () => {
	const intl = useIntl();
	const { closeModal } = useModalContext();
	const [submitMemberIndex, setSubmitMemberIndex] = useState(0);
	const [membersCount, setMembersCount] = useState(0);
	const { openSnackbar } = useSnackbar();
	const { mutateAsync: inviteMember } = useApiMutation(inviteMemberMutation, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: Boolean(result.inviteMember.ok),
				toastMessages: {
					success: intl.formatMessage({ id: 'form.settings.invite-member.create.success' }),
					error: intl.formatMessage({ id: 'form.settings.invite-member.create.error' }),
				},
				errorMessage: result.inviteMember.errorMessage,
				openSnackbar,
			});
		},
	});

	const form = useTypedForm({
		defaultValues: {
			members: [
				{
					name: '',
					email: '',
					teamId: '',
				},
			],
		},
		schema: createFormSchema(intl),
	});

	const { fields, append, remove } = useFieldArray({
		name: 'members',
		control: form.control,
	});

	const onSubmit = async ({ members }: FormValues) => {
		setMembersCount(members.length);
		for (let i = 0; i < members.length; i++) {
			const element: FormValues['members'][number] = {
				...members[i],
				teamId: Boolean(members[i].teamId) ? members[i].teamId : null,
			};
			try {
				await inviteMember(element);
				setSubmitMemberIndex(i + 1);
				remove(0); // Removes successfully invited member from form
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
			} catch (error) {
				//Prevent loop from continuing if there is an error
				return;
			}
		}

		setSubmitMemberIndex(0);
		setMembersCount(0);
		form.reset();
	};

	return (
		<form noValidate onSubmit={form.handleSubmit(onSubmit)}>
			<ModalContent title={intl.formatMessage({ id: 'form.settings.invite-member.title' })}>
				{fields.map(({ id }, index) => (
					<Stack gap={2} mb={4} key={id}>
						{index !== 0 && (
							<IconButton
								sx={{ alignSelf: 'flex-end' }}
								type={'button'}
								onClick={() => remove(index)}
								disabled={form.formState.isSubmitting}
							>
								<DeleteOutlineOutlined color={'error'} />
							</IconButton>
						)}
						<FormField
							form={form}
							name={`members.${index}.name`}
							label={intl.formatMessage({ id: 'form.settings.invite-member.name' })}
							render={(props) => <OutlinedInput {...props} />}
						/>
						<FormField
							form={form}
							name={`members.${index}.email`}
							label={intl.formatMessage({ id: 'form.settings.invite-member.email' })}
							render={(props) => <OutlinedInput {...props} />}
						/>
						<ControlledFormField
							form={form}
							name={`members.${index}.teamId`}
							label={intl.formatMessage({ id: 'form.settings.invite-member.team' })}
							render={(props) => <TeamSelect {...props} />}
						/>
					</Stack>
				))}
				<Button
					startIcon={<Add sx={{ color: (theme) => theme.palette[getNightOrDayPalette(theme)][900] }} />}
					disabled={form.formState.isSubmitting}
					type={'button'}
					onClick={() => append({ name: '', email: '', teamId: '' })}
				>
					<Typography color={(theme) => theme.palette[getNightOrDayPalette(theme)][900]}>
						<FormattedMessage id={'form.settings.invite-member.additional-member'} />
					</Typography>
				</Button>
				<FormErrors form={form} />
				{membersCount > 0 && (
					<Stack>
						<LinearProgress variant={'determinate'} value={(submitMemberIndex / membersCount) * 100} />
					</Stack>
				)}
			</ModalContent>
			<ModalActions
				cancelButtonText={intl.formatMessage({ id: 'common.close' })}
				handleCancelButtonClick={() => closeModal()}
				handlePrimaryButtonClick={form.handleSubmit(onSubmit)}
				primaryButtonText={intl.formatMessage({ id: 'members.invite' })}
				disablePrimaryButton={form.formState.isSubmitting}
			/>
		</form>
	);
};

export { InviteMembersModal, inviteMembersModalSchema };
